/**
 * Fleet realtime hook.
 *
 * Loads the fleet and the bookings covering a date window, then keeps both in
 * step with Appwrite Realtime so a booking made on the front desk appears on
 * everyone else's Gantt without a refresh.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { client, DATABASE_ID, COLLECTIONS } from '../services/appwrite';
import { fleetVehicleList, fleetBookingList } from '../services/fleet';

/** Appwrite has used both documents.* and rows.* event names; match either. */
const eventKind = (events = []) => {
  const matched = events.find((e) => /\.(create|update|delete)$/.test(e));
  return matched ? matched.split('.').pop() : null;
};

const upsert = (list, row) => {
  const index = list.findIndex((item) => item.$id === row.$id);
  if (index === -1) return [...list, row];
  const next = [...list];
  next[index] = { ...next[index], ...row };
  return next;
};

const remove = (list, id) => list.filter((item) => item.$id !== id);

export function useFleetRealtime({ from, to } = {}) {
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);

  // The window moves as the user pages through the timeline. Held in a ref so
  // the long-lived subscription can read the current value without being torn
  // down and rebuilt on every navigation.
  const windowRef = useRef({ from, to });
  windowRef.current = { from, to };

  const fromKey = from ? new Date(from).toISOString() : null;
  const toKey = to ? new Date(to).toISOString() : null;

  const load = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      const [vehicleRows, bookingRows] = await Promise.all([
        fleetVehicleList(),
        fleetBookingList({ from: fromKey, to: toKey })
      ]);
      setVehicles(vehicleRows);
      setBookings(bookingRows);
      setError(null);
    } catch (err) {
      console.error('Failed to load fleet:', err);
      setError(err?.message || 'Failed to load fleet');
    } finally {
      setLoading(false);
    }
  }, [fromKey, toKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const channels = [
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.FLEET_VEHICLES}.documents`,
      `databases.${DATABASE_ID}.collections.${COLLECTIONS.FLEET_BOOKINGS}.documents`
    ];

    let unsubscribe;
    try {
      unsubscribe = client.subscribe(channels, (response) => {
        const kind = eventKind(response.events);
        const row = response.payload;
        if (!kind || !row?.$id) return;

        const isVehicle = response.events.some((e) => e.includes(COLLECTIONS.FLEET_VEHICLES));
        // Deleting is soft, so a delete arrives as an update carrying deletedAt,
        // and a restore as an update clearing it. Either way the row's own
        // state decides, not the event name.
        const gone = kind === 'delete' || Boolean(row.deletedAt);

        if (isVehicle) {
          setVehicles((prev) => (gone ? remove(prev, row.$id) : upsert(prev, row)));
          return;
        }

        if (gone) {
          setBookings((prev) => remove(prev, row.$id));
          return;
        }

        // A booking can be moved out of the window we are looking at, in which
        // case it should disappear rather than linger at its old position.
        const { from: winFrom, to: winTo } = windowRef.current;
        if (winFrom && winTo) {
          const start = new Date(row.scheduledStart);
          const end = new Date(row.scheduledEnd);
          const stillOut = row.status === 'out';
          const inWindow = start <= new Date(winTo) && end >= new Date(winFrom);
          if (!inWindow && !stillOut) {
            setBookings((prev) => remove(prev, row.$id));
            return;
          }
        }

        setBookings((prev) => upsert(prev, row));
      });
      setLive(true);
    } catch (err) {
      console.warn('[Fleet] Realtime unavailable:', err?.message);
      setLive(false);
    }

    return () => {
      setLive(false);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  /** Apply a change locally before the server echo, so the UI never lags. */
  const applyLocal = useCallback((row, { deleted = false } = {}) => {
    if (!row?.$id) return;
    setBookings((prev) => (deleted ? remove(prev, row.$id) : upsert(prev, row)));
  }, []);

  const applyLocalVehicle = useCallback((row, { deleted = false } = {}) => {
    if (!row?.$id) return;
    setVehicles((prev) => (deleted ? remove(prev, row.$id) : upsert(prev, row)));
  }, []);

  return {
    vehicles,
    bookings,
    loading,
    error,
    live,
    refresh: () => load({ quiet: true }),
    applyLocal,
    applyLocalVehicle,
    setVehicles
  };
}

export default useFleetRealtime;
